import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";

/**
 * ScheduledReportsPage.jsx — full regeneration (CSV-aware, CORS-friendly, no credentials)
 *
 * Standalone page for managing Scheduled Reports tied to a client subdomain.
 *
 * Notes:
 * • Uses fetch with credentials: 'omit' so wildcard CORS ("*") continues to work.
 * • API surface (mini-API): clientDetails.php?sr=list|logs (GET), upsert|toggle|delete|run_now (POST)
 * • Default apiBase targets production: https://house.ogendeavors.com/api/
 * • Tailwind classes for simple styling; no external UI libs required.
 * • IMPORTANT: Investor emails are kept ONLY in `investorRecipient` and NOT merged into `recipients`.
 * • IMPORTANT: New `output_format` field supports html|csv.
 * • UI change retained: no "Run As User (Investor)" section.
 */

/** Utility: build absolute URL to clientDetails.php with ?sr=... params */
function buildSrUrl(apiBase, action, params = {}) {
  const base = apiBase || "https://house.ogendeavors.com/api/";
  const url = new URL("clientDetails.php", base);
  url.searchParams.set("sr", action);
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  }
  return url.toString();
}

/** Utility: normalize recipients CSV (emails only) */
function normalizeRecipients(input) {
  if (!input) return "";
  const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  const found = String(input).match(EMAIL_RE) || [];
  const seen = new Set();
  const deduped = [];
  for (const e of found.map((s) => s.trim())) {
    const k = e.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      deduped.push(e);
    }
  }
  return deduped.join(", ");
}

/** Utility: parse CSV list → array (trims, drops blanks) */
function parseCsv(str) {
  if (!str) return [];
  return String(str)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Utility: array of emails -> normalized CSV (dedupe, case-insens.) */
function toCsvDedup(emails) {
  return normalizeRecipients((emails || []).join(","));
}

/** Utility: dedupe string IDs (case-sensitive), return CSV */
function idsToCsvDedup(ids) {
  const seen = new Set();
  const out = [];
  (ids || []).forEach((id) => {
    const s = String(id ?? "").trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out.join(",");
}

/** Utility: human label for HH:MM (24h -> 12h) */
function timeLabel(h, m) {
  const hh = Number(h) || 0;
  const mm = Number(m) || 0;
  const am = hh < 12;
  const h12 = hh % 12 || 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

/** Utility: frequency text */
function frequencyLabel(freq, dow, dom) {
  if (freq === "weekly") return `Weekly (${dow || "0-6"})`;
  if (freq === "monthly") return `Monthly (day ${dom || 1})`;
  return "Daily";
}

/** Utility: format label for output format */
function formatLabel(fmt) {
  return String(fmt || "html").toLowerCase() === "csv" ? "CSV" : "HTML";
}

/** Utility: user label "FullName - Email" (fallbacks, marks no email) */
function userDisplay(u) {
  if (!u) return "";
  const name = u.FullName && u.FullName.trim().length ? u.FullName.trim() : "";
  const email = (u.Email || "").trim();
  if (name && email) return `${name} - ${email}`;
  if (name && !email) return `${name} - (no email)`;
  if (!name && email) return email;
  return "(no name / no email)";
}

/** Tailwind input helper */
const getInputClasses = (theme) =>
  `border rounded px-2 py-1 w-full ${
    theme === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "bg-white text-gray-900 border-gray-300"
  }`;

/** Utility: format next_run_at (stored as UTC) into the schedule's timezone */
function formatNextRun(next_run_at, timezone) {
  if (!next_run_at) return "—";
  try {
    let iso = String(next_run_at).trim();
    if (!iso) return "—";

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)) {
      iso = iso.replace(" ", "T") + "Z";
    }

    const d = new Date(iso);
    if (isNaN(d.getTime())) return next_run_at;

    const fmt = new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: timezone || "UTC",
    });

    return fmt.format(d);
  } catch {
    return next_run_at;
  }
}

/** RecipientsPicker: chips + typeahead suggestions (emails). Free-type supported. */
function RecipientsPicker({ value, onChange, users, theme }) {
  const [input, setInput] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const tokens = useMemo(() => parseCsv(normalizeRecipients(value)), [value]);

  const existingSet = useMemo(
    () => new Set(tokens.map((t) => t.toLowerCase())),
    [tokens]
  );

  const allSuggestions = useMemo(() => {
    const arr = Array.isArray(users) ? users : [];
    return arr
      .filter((u) => (u?.Email || "").trim())
      .map((u) => ({
        email: u.Email.trim(),
        label: userDisplay(u),
      }));
  }, [users]);

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    return allSuggestions
      .filter(
        (s) =>
          !existingSet.has(s.email.toLowerCase()) &&
          (s.email.toLowerCase().includes(q) ||
            s.label.toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [input, allSuggestions, existingSet]);

  const commit = useCallback(
    (raw) => {
      const normalized = normalizeRecipients(raw);
      if (!normalized) return;
      const add = parseCsv(normalizeRecipients(raw)).filter(
        (e) => !existingSet.has(e.toLowerCase())
      );
      if (add.length === 0) return;
      const next = normalizeRecipients([...tokens, ...add].join(","));
      onChange?.(next);
    },
    [existingSet, onChange, tokens]
  );

  const remove = (email) => {
    const next = tokens.filter((t) => t.toLowerCase() !== email.toLowerCase());
    onChange?.(normalizeRecipients(next.join(",")));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (["Enter", "Tab", ",", ";"].includes(e.key)) {
      e.preventDefault();
      if (input.trim()) {
        commit(input);
        setInput("");
      }
    } else if (e.key === "Backspace" && !input && tokens.length) {
      remove(tokens[tokens.length - 1]);
    }
  };

  const flushInput = useCallback(() => {
    if (input.trim()) {
      commit(input);
      setInput("");
    }
  }, [input, commit]);

  useEffect(() => {
    const handler = (ev) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target)) flushInput();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [flushInput]);

  return (
    <div
      ref={containerRef}
      className={`border rounded px-2 py-1 w-full ${
        theme === "dark"
          ? "bg-neutral-800 text-gray-100 border-neutral-700"
          : "bg-white text-gray-900 border-gray-300"
      }`}
    >
      <div className="flex flex-wrap gap-1">
        {tokens.map((t) => (
          <span
            key={t}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${
              theme === "dark"
                ? "border-neutral-700 bg-neutral-900 text-gray-200"
                : "border-gray-300 bg-gray-50 text-gray-800"
            }`}
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className={
                theme === "dark" ? "hover:text-red-400" : "hover:text-red-600"
              }
              aria-label={`Remove ${t}`}
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={flushInput}
          className={`flex-1 min-w-[120px] bg-transparent outline-none text-sm ${
            theme === "dark" ? "placeholder-gray-500" : "placeholder-gray-400"
          }`}
          placeholder={tokens.length ? "" : "Type email, press Enter…"}
        />
      </div>

      {filteredSuggestions.length > 0 && (
        <ul
          className={`mt-1 max-h-48 overflow-auto rounded border text-sm shadow ${
            theme === "dark"
              ? "border-neutral-700 bg-neutral-900"
              : "border-gray-200 bg-white"
          }`}
          role="listbox"
        >
          {filteredSuggestions.map((s) => (
            <li
              key={s.email}
              role="option"
              tabIndex={0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                commit(s.email);
                setInput("");
                inputRef.current?.focus();
              }}
              className={
                theme === "dark"
                  ? "px-2 py-1 cursor-pointer hover:bg-neutral-800"
                  : "px-2 py-1 cursor-pointer hover:bg-gray-50"
              }
              title={s.email}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ScheduledReportsPage({
  subdomain,
  users,
  tags,
  leases, // available if needed later
  apiBase = "https://house.ogendeavors.com/api/",
  theme,
}) {
  function newScheduleTemplate(currentSubdomain) {
    return {
      id: 0,
      subdomain: currentSubdomain || "",
      report_code: "PROD",
      output_format: "html",
      recipients: "",
      selected_user_ids: [],
      selected_investor_user_ids: [],
      frequency: "daily",
      time_hour: 7,
      time_minute: 0,
      timezone: "America/Chicago",
      days_of_week: "1,2,3,4,5",
      day_of_month: 1,
      enabled: 1,
      filter_user: "",
      filter_pumpers: [],
      filter_tags: [],
      params_json: null,
      investorRecipient: "",
      investorRecipientUserIDs: "",
    };
  }

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [logs, setLogs] = useState({ open: false, rows: [], forId: 0 });
  const [search, setSearch] = useState("");

  const safeUsers = useMemo(() => (Array.isArray(users) ? users : []), [users]);
  const safeTags = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);

  const usersById = useMemo(() => {
    const map = new Map();
    safeUsers.forEach((u) => map.set(String(u.UserID ?? ""), u));
    return map;
  }, [safeUsers]);

  const investorUsers = useMemo(
    () => safeUsers.filter((u) => (u?.Role || "").toUpperCase() === "I"),
    [safeUsers]
  );

  const pumperUsers = useMemo(
    () => safeUsers.filter((u) => (u?.Role || "").toUpperCase() === "P"),
    [safeUsers]
  );

  const allUsersForPicker = useMemo(() => safeUsers, [safeUsers]);

  const canSave = useMemo(() => {
    if (!form) return false;
    const hh = Number(form.time_hour);
    const mm = Number(form.time_minute);
    return (
      !!form.subdomain &&
      !!form.report_code &&
      !!form.frequency &&
      (form.output_format === "html" || form.output_format === "csv") &&
      Number.isFinite(hh) &&
      hh >= 0 &&
      hh <= 23 &&
      Number.isFinite(mm) &&
      mm >= 0 &&
      mm <= 59
    );
  }, [form]);

  const apiGet = useCallback(
    async (action, params = {}) => {
      const url = buildSrUrl(apiBase, action, params);
      const res = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`GET ${action} failed (${res.status})`);
      const j = await res.json();
      if (Array.isArray(j)) return j;
      if (j && typeof j === "object" && "ok" in j) {
        if (!j.ok) throw new Error(j.error || "error");
        return j.data ?? [];
      }
      return j?.data ?? j ?? [];
    },
    [apiBase]
  );

  /** Utility: report code → human label */
function reportLabel(code) {
  const c = String(code || "").toUpperCase();
  if (c === "PROD") return "Production";
  if (c === "TIME") return "Time";
  if (c === "WTEST") return "Well Tests";
  return c || "Unknown";
}
  const apiPost = useCallback(
    async (action, body = {}) => {
      const url = buildSrUrl(apiBase, action);
      const res = await fetch(url, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`POST ${action} failed (${res.status})`);
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || "error");
      return j.data;
    },
    [apiBase]
  );

  const load = useCallback(async () => {
    if (!subdomain) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiGet("list", { sub: subdomain });
      const normalized = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        output_format:
          String(r.output_format || "html").toLowerCase() === "csv"
            ? "csv"
            : "html",
      }));
      setRows(normalized);
    } catch (e) {
      setError(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiGet, subdomain]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const filtersPretty = [r.filter_user, r.filter_pumper, r.filter_tags]
        .filter(Boolean)
        .join(" ");
      const hay = [
        r.report_code,
        r.output_format,
        r.recipients,
        r.investorRecipient || "",
        r.investorRecipientUserIDs || "",
        r.frequency,
        r.timezone,
        r.next_run_at,
        r.subdomain,
        filtersPretty,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const openNew = () => setForm(newScheduleTemplate(subdomain));

  const userIdsToEmails = useCallback(
    (ids) => {
      const emails = [];
      (ids || []).forEach((id) => {
        const u = usersById.get(String(id));
        const e = (u?.Email || "").trim();
        if (e) emails.push(e);
      });
      return emails;
    },
    [usersById]
  );

  const derived = useMemo(() => {
    if (!form) {
      return {
        mergedRecipientsCsv: "",
        investorCsv: "",
        investorIdsCsv: "",
      };
    }

    const freeTyped = parseCsv(normalizeRecipients(form.recipients));
    const fromUsers = userIdsToEmails(form.selected_user_ids || []);
    const fromInvestors = userIdsToEmails(form.selected_investor_user_ids || []);

    const investorCsv = toCsvDedup(fromInvestors);
    const mergedRecipientsCsv = toCsvDedup([...freeTyped, ...fromUsers]);
    const investorIdsCsv = idsToCsvDedup(form.selected_investor_user_ids || []);

    return { mergedRecipientsCsv, investorCsv, investorIdsCsv };
  }, [form, userIdsToEmails]);

  const save = async () => {
    if (!form) return;

    setSaving(true);
    setError("");

    try {
      const body = {
        ...form,
        output_format:
          String(form.output_format || "html").toLowerCase() === "csv"
            ? "csv"
            : "html",
        recipients: derived.mergedRecipientsCsv,
        investorRecipient: derived.investorCsv,
        investorRecipientUserIDs: derived.investorIdsCsv,
        filter_tags: Array.isArray(form.filter_tags)
          ? form.filter_tags.join(",")
          : form.filter_tags || "",
        filter_pumper: Array.isArray(form.filter_pumpers)
          ? form.filter_pumpers.join(",")
          : form.filter_pumpers || "",
      };

      delete body.filter_pumpers;
      delete body.selected_user_ids;
      delete body.selected_investor_user_ids;

      if (body.params_json && typeof body.params_json !== "string") {
        body.params_json = JSON.stringify(body.params_json);
      }

      await apiPost("upsert", body);
      setForm(null);
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r) => {
    try {
      await apiPost("toggle", { id: r.id, enabled: r.enabled ? 0 : 1 });
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await apiPost("delete", { id: r.id });
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const runNow = async (r) => {
    try {
      await apiPost("run_now", { id: r.id });
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const openLogs = async (r) => {
    try {
      const data = await apiGet("logs", { scheduled_id: r.id });
      setLogs({ open: true, rows: Array.isArray(data) ? data : [], forId: r.id });
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const renderFilterLabels = (r) => {
    const parts = [];

    if (r.filter_user) {
      const u = usersById.get(String(r.filter_user)) || null;
      parts.push(`User: ${u ? userDisplay(u) : r.filter_user}`);
    }

    if (r.filter_pumper) {
      const ids = parseCsv(r.filter_pumper);
      const labels = ids
        .map((id) => {
          const u = usersById.get(String(id));
          return u ? userDisplay(u) : id;
        })
        .join("; ");
      parts.push(labels ? `Pumpers: ${labels}` : "");
    }

    if (r.filter_tags) {
      parts.push(`Tags: ${r.filter_tags}`);
    }

    const s = parts.filter(Boolean).join(" | ");
    return s || "—";
  };

  const currentFormSummary = useMemo(() => {
    if (!form) return null;
    return {
      recipients: derived.mergedRecipientsCsv || "—",
      investorRecipient: derived.investorCsv || "—",
      investorIds: derived.investorIdsCsv || "—",
    };
  }, [form, derived]);

  return (
    <div
      className={`p-4 max-w-6xl mx-auto ${
        theme === "dark"
          ? "text-gray-100 bg-gray-800"
          : "text-gray-900 bg-white"
      }`}
    >
      <header className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Scheduled Reports</h1>
          <p
            className={`text-sm ${
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Subdomain:{" "}
            <span className="font-mono">{subdomain || "(none)"}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className={getInputClasses(theme)}
          />
          <button
            className={`px-3 py-2 border rounded ${
              theme === "dark"
                ? "bg-gray-700 text-gray-100 border-gray-600"
                : "bg-white text-gray-900 border-gray-300"
            }`}
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Reload"}
          </button>
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white"
            onClick={openNew}
          >
            New schedule
          </button>
        </div>
      </header>

      {error && (
        <div
          className={`mb-3 p-3 rounded border text-sm ${
            theme === "dark"
              ? "border-red-700/50 bg-red-900/20 text-red-200"
              : "border-red-300 bg-red-50 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      <div
        className={`border rounded ${
          theme === "dark" ? "border-gray-600" : "border-gray-200"
        } overflow-x-auto`}
      >
        <table className="min-w-full text-sm">
          <thead
            className={
              theme === "dark"
                ? "bg-gray-700 text-gray-100"
                : "bg-gray-50 text-gray-800"
            }
          >
            <tr>
              <th className="text-left p-2">Subdomain</th>
              <th className="text-left p-2">Report</th>
              <th className="text-left p-2">Format</th>
              <th className="text-left p-2">Recipients</th>
              <th className="text-left p-2">Investor Recipients</th>
              <th className="text-left p-2">Investor IDs</th>
              <th className="text-left p-2">Filters</th>
              <th className="text-left p-2">Frequency</th>
              <th className="text-left p-2">Time</th>
              <th className="text-left p-2">TZ</th>
              <th className="text-left p-2">Next Run (Schedule TZ)</th>
              <th className="text-left p-2">Enabled</th>
              <th
                className={`text-left p-2 sticky right-0 z-0 ${
                  theme === "dark" ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={theme === "dark" ? "bg-gray-800" : "bg-white"}>
            {loading && (
              <tr>
                <td className="p-3" colSpan={13}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td className="p-3" colSpan={13}>
                  No schedules.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t ${
                    theme === "dark" ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  <td className="p-2 font-mono">{r.subdomain}</td>
                <td className="p-2">
                  {reportLabel(r.report_code)}
                </td>
                  <td className="p-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${
                        String(r.output_format || "html").toLowerCase() === "csv"
                          ? theme === "dark"
                            ? "bg-emerald-900/30 border-emerald-700 text-emerald-200"
                            : "bg-emerald-50 border-emerald-300 text-emerald-700"
                          : theme === "dark"
                          ? "bg-blue-900/30 border-blue-700 text-blue-200"
                          : "bg-blue-50 border-blue-300 text-blue-700"
                      }`}
                    >
                      {formatLabel(r.output_format)}
                    </span>
                  </td>
                  <td className="p-2 max-w-[320px] truncate" title={r.recipients}>
                    {r.recipients || "—"}
                  </td>
                  <td
                    className="p-2 max-w-[320px] truncate"
                    title={r.investorRecipient || ""}
                  >
                    {r.investorRecipient || "—"}
                  </td>
                  <td
                    className="p-2 max-w-[240px] truncate"
                    title={r.investorRecipientUserIDs || ""}
                  >
                    {r.investorRecipientUserIDs || "—"}
                  </td>
                  <td className="p-2">{renderFilterLabels(r)}</td>
                  <td className="p-2">
                    {frequencyLabel(r.frequency, r.days_of_week, r.day_of_month)}
                  </td>
                  <td className="p-2">{timeLabel(r.time_hour, r.time_minute)}</td>
                  <td className="p-2">{r.timezone}</td>
                  <td className="p-2">
                    {r.next_run_at
                      ? `${formatNextRun(r.next_run_at, r.timezone)}${
                          r.timezone ? ` (${r.timezone})` : ""
                        }`
                      : "—"}
                  </td>
                  <td className="p-2">{r.enabled ? "Yes" : "No"}</td>
                  <td
                    className={`p-2 sticky right-0 z-0 ${
                      theme === "dark" ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <div className="flex flex-wrap gap-2">
                      <button
                        className={`px-2 py-1 border rounded ${
                          theme === "dark"
                            ? "bg-gray-700 text-gray-100 border-gray-600"
                            : "bg-white text-gray-900 border-gray-300"
                        }`}
                        onClick={() => openLogs(r)}
                      >
                        Logs
                      </button>
                      <button
                        className={`px-2 py-1 border rounded ${
                          theme === "dark"
                            ? "bg-gray-700 text-gray-100 border-gray-600"
                            : "bg-white text-gray-900 border-gray-300"
                        }`}
                        onClick={() => runNow(r)}
                      >
                        Run now
                      </button>
                      <button
                        className={`px-2 py-1 border rounded ${
                          theme === "dark"
                            ? "bg-gray-700 text-gray-100 border-gray-600"
                            : "bg-white text-gray-900 border-gray-300"
                        }`}
                        onClick={() => toggle(r)}
                      >
                        {r.enabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        className={`px-2 py-1 border rounded ${
                          theme === "dark"
                            ? "bg-gray-700 text-gray-100 border-gray-600"
                            : "bg-white text-gray-900 border-gray-300"
                        }`}
                        onClick={() =>
                          setForm({
                            ...r,
                            output_format:
                              String(r.output_format || "html").toLowerCase() ===
                              "csv"
                                ? "csv"
                                : "html",
                            filter_tags: r.filter_tags ? parseCsv(r.filter_tags) : [],
                            filter_pumpers: r.filter_pumper
                              ? parseCsv(r.filter_pumper)
                              : [],
                            selected_user_ids: [],
                            selected_investor_user_ids: r.investorRecipientUserIDs
                              ? parseCsv(r.investorRecipientUserIDs)
                              : [],
                            recipients: normalizeRecipients(r.recipients || ""),
                            investorRecipient: normalizeRecipients(
                              r.investorRecipient || ""
                            ),
                            investorRecipientUserIDs:
                              r.investorRecipientUserIDs || "",
                          })
                        }
                      >
                        Edit
                      </button>
                      <button
                        className={`px-2 py-1 border rounded ${
                          theme === "dark"
                            ? "text-red-300 border-red-700/50 bg-gray-700"
                            : "text-red-600 border-red-300 bg-white"
                        }`}
                        onClick={() => del(r)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div
          className={`${
            theme === "dark"
              ? "bg-black bg-opacity-80"
              : "bg-black bg-opacity-50"
          } fixed inset-0 z-40 grid place-items-center p-4`}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`rounded-xl w-full max-w-3xl p-4 space-y-3 ${
              theme === "dark"
                ? "bg-gray-800 text-gray-100"
                : "bg-white text-gray-900"
            } max-h-[85vh] overflow-auto`}
          >
            <div
              className="flex items-center justify-between sticky top-0 pb-2 mb-2"
              style={{ background: "inherit" }}
            >
              <h2 className="text-lg font-semibold">
                {form.id ? "Edit schedule" : "New schedule"}
              </h2>
              <button
                onClick={() => setForm(null)}
                className={
                  theme === "dark"
                    ? "px-2 py-1 rounded hover:bg-gray-700"
                    : "px-2 py-1 rounded hover:bg-gray-100"
                }
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm mb-1">Subdomain</label>
                <input
                  value={form.subdomain}
                  onChange={(e) =>
                    setForm({ ...form, subdomain: e.target.value })
                  }
                  className={`${getInputClasses(theme)} font-mono`}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Report</label>
                <select
                  value={form.report_code}
                  onChange={(e) =>
                    setForm({ ...form, report_code: e.target.value })
                  }
                  className={getInputClasses(theme)}
                >
                  <option value="PROD">Production</option>
                  <option value="TIME">Time</option>
                  <option value="WTEST">Well Tests</option>
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm mb-1">Output Format</label>
                <select
                  value={form.output_format || "html"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      output_format:
                        e.target.value === "csv" ? "csv" : "html",
                    })
                  }
                  className={getInputClasses(theme)}
                >
                  <option value="html">HTML email</option>
                  <option value="csv">CSV attachment</option>
                </select>
                <p
                  className={`mt-1 text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  HTML sends the rendered report in the email body. CSV sends a
                  summary email with the report attached as a CSV file.
                </p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm mb-1">
                  Recipients (type emails or pick from users)
                </label>
                <RecipientsPicker
                  value={form.recipients}
                  onChange={(v) => setForm({ ...form, recipients: v })}
                  users={allUsersForPicker}
                  theme={theme}
                />
                <p
                  className={`mt-1 text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Suggestions show as <span className="italic">FullName - Email</span>.
                  You can also paste or type any email and press Enter.
                </p>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Add Recipients from Users
                </label>
                <select
                  multiple
                  value={form.selected_user_ids || []}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map(
                      (o) => o.value
                    );
                    setForm({ ...form, selected_user_ids: opts });
                  }}
                  className={`${getInputClasses(theme)} h-24`}
                >
                  {allUsersForPicker.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {userDisplay(u)}
                    </option>
                  ))}
                </select>
                <p
                  className={`mt-1 text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Selected users’ <span className="italic">emails</span> are
                  merged into <code>recipients</code> on save. Users without
                  emails are ignored.
                </p>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Investor Primary Recipient(s) (Role = I)
                </label>
                <select
                  multiple
                  value={form.selected_investor_user_ids || []}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map(
                      (o) => o.value
                    );
                    setForm({ ...form, selected_investor_user_ids: opts });
                  }}
                  className={`${getInputClasses(theme)} h-24`}
                >
                  {investorUsers.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {userDisplay(u)}
                    </option>
                  ))}
                </select>
                <p
                  className={`mt-1 text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Populates <code>investorRecipient</code> (emails) and{" "}
                  <code>investorRecipientUserIDs</code> (IDs).{" "}
                  <strong>Investor emails are not added to</strong>{" "}
                  <code>recipients</code>.
                </p>
              </div>

              <div className="col-span-2">
                <div
                  className={`rounded border p-3 text-xs ${
                    theme === "dark"
                      ? "border-gray-600 bg-gray-900/30 text-gray-300"
                      : "border-gray-200 bg-gray-50 text-gray-700"
                  }`}
                >
                  <div className="font-semibold mb-2">Save Preview</div>
                  <div>
                    <span className="font-medium">Recipients:</span>{" "}
                    {currentFormSummary?.recipients}
                  </div>
                  <div className="mt-1">
                    <span className="font-medium">Investor Recipients:</span>{" "}
                    {currentFormSummary?.investorRecipient}
                  </div>
                  <div className="mt-1">
                    <span className="font-medium">Investor IDs:</span>{" "}
                    {currentFormSummary?.investorIds}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Filter by Pumper(s)</label>
                <select
                  multiple
                  value={form.filter_pumpers || []}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map(
                      (o) => o.value
                    );
                    setForm({ ...form, filter_pumpers: opts });
                  }}
                  className={`${getInputClasses(theme)} h-24`}
                >
                  {pumperUsers.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {userDisplay(u)}
                    </option>
                  ))}
                </select>
                <p
                  className={`mt-1 text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Multiple pumpers allowed. Saved as a CSV of{" "}
                  <span className="italic">UserID</span>s in{" "}
                  <code>filter_pumper</code>.
                </p>
              </div>

              <div>
                <label className="block text-sm mb-1">Filter by Tags</label>
                <select
                  multiple
                  value={form.filter_tags || []}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map(
                      (o) => o.value
                    );
                    setForm({ ...form, filter_tags: opts });
                  }}
                  className={`${getInputClasses(theme)} h-24`}
                >
                  {safeTags.map((t) => (
                    <option key={t.TagID ?? t.TagDesc} value={t.TagDesc}>
                      {t.TagDesc}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1">Frequency</label>
                <select
                  value={form.frequency}
                  onChange={(e) =>
                    setForm({ ...form, frequency: e.target.value })
                  }
                  className={getInputClasses(theme)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1">Timezone</label>
                <input
                  value={form.timezone}
                  onChange={(e) =>
                    setForm({ ...form, timezone: e.target.value })
                  }
                  className={getInputClasses(theme)}
                  placeholder="IANA TZ e.g. America/Chicago"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Hour (0-23)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={form.time_hour}
                  onChange={(e) =>
                    setForm({ ...form, time_hour: Number(e.target.value) })
                  }
                  className={getInputClasses(theme)}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Minute</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={form.time_minute}
                  onChange={(e) =>
                    setForm({ ...form, time_minute: Number(e.target.value) })
                  }
                  className={getInputClasses(theme)}
                />
              </div>

              {form.frequency === "weekly" && (
                <div className="col-span-2">
                  <label className="block text-sm mb-1">
                    Days of week (0=Sun..6=Sat)
                  </label>
                  <input
                    value={form.days_of_week || ""}
                    onChange={(e) =>
                      setForm({ ...form, days_of_week: e.target.value })
                    }
                    className={getInputClasses(theme)}
                  />
                </div>
              )}

              {form.frequency === "monthly" && (
                <div>
                  <label className="block text-sm mb-1">
                    Day of month (1-28)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={28}
                    value={form.day_of_month || 1}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        day_of_month: Number(e.target.value),
                      })
                    }
                    className={getInputClasses(theme)}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm mb-1">Enabled</label>
                <select
                  value={form.enabled}
                  onChange={(e) =>
                    setForm({ ...form, enabled: Number(e.target.value) })
                  }
                  className={getInputClasses(theme)}
                >
                  <option value={1}>Yes</option>
                  <option value={0}>No</option>
                </select>
              </div>
            </div>

            <div
              className="flex justify-end gap-2 pt-2 sticky bottom-0 mt-2"
              style={{ background: "inherit" }}
            >
              <button
                className={`px-3 py-2 border rounded ${
                  theme === "dark"
                    ? "bg-gray-700 text-gray-100 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-900 border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-2 rounded text-white ${
                  canSave && !saving
                    ? "bg-blue-600 hover:bg-blue-700"
                    : theme === "dark"
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
                onClick={save}
                disabled={!canSave || saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {logs.open && (
        <div
          className={`${
            theme === "dark"
              ? "bg-black bg-opacity-80"
              : "bg-black bg-opacity-50"
          } fixed inset-0 z-40 grid place-items-center p-4`}
          onClick={() => setLogs({ open: false, rows: [], forId: 0 })}
        >
          <div
            className={`rounded-xl w-full max-w-3xl p-4 ${
              theme === "dark"
                ? "bg-gray-800 text-gray-100"
                : "bg-white text-gray-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">
                Logs for schedule #{logs.forId}
              </h2>
              <button
                className={
                  theme === "dark"
                    ? "px-2 py-1 rounded hover:bg-gray-700"
                    : "px-2 py-1 rounded hover:bg-gray-100"
                }
                onClick={() => setLogs({ open: false, rows: [], forId: 0 })}
              >
                ✕
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto">
              <table className="min-w-full text-sm">
                <thead
                  className={
                    theme === "dark"
                      ? "bg-gray-700 text-gray-100"
                      : "bg-gray-50 text-gray-800"
                  }
                >
                  <tr>
                    <th className="text-left p-2">When</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Message</th>
                  </tr>
                </thead>
                <tbody
                  className={theme === "dark" ? "bg-gray-800" : "bg-white"}
                >
                  {logs.rows.length === 0 && (
                    <tr>
                      <td className="p-2" colSpan={3}>
                        No logs.
                      </td>
                    </tr>
                  )}
                  {logs.rows.map((l) => (
                    <tr
                      key={l.id}
                      className={`border-t ${
                        theme === "dark" ? "border-gray-600" : "border-gray-200"
                      }`}
                    >
                      <td className="p-2">{l.started_at}</td>
                      <td className="p-2">{l.status}</td>
                      <td className="p-2 whitespace-pre-wrap">
                        {l.message || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}